<template>
  <div class="pay">
    <b-button class="btn-rainbow purchase" v-if="!isLoggedIn" :to="{name: 'account-login', query: {next: $route.fullPath }}">
      <i class="sl-icon-login" v-if="this.live"></i>
      <font-awesome-icon icon="music" v-if="!this.live" />
      &nbsp;
      <div class="label"><span class="normal">{{ purchaseLabel }}</span></div>
    </b-button>

    <b-button variant="secondary" class="purchase" @click="flashUnavailable" v-if="isLoggedIn && !isAvailable">
      <i class="sl-icon-login" v-if="this.live"></i>
      <font-awesome-icon icon="music" v-if="!this.live" />
      <div class="label">&nbsp; {{ unavailableLabel }}</div>
    </b-button>

    <b-button class="btn-rainbow purchase" :id="btnId" v-if="isLoggedIn && isAvailable && !hasAccessTo">
      <i class="sl-icon-login" v-if="this.live"></i>
      <font-awesome-icon icon="music" v-if="!this.live" />
      &nbsp;
      <div class="label"><span class="normal">{{ purchaseLabel }}</span></div>
    </b-button>

    <b-popover :target="btnId" triggers="click blur" placement="bottom" title="" ref="popover" v-if="isLoggedIn && isAvailable && !hasAccessTo">
      <div class="buyPopover">
        <b-form-group v-if="canPayMore" :label="payLabel"
                      class="text-center"
                      :description="minAmountPlaceholder" :state="inputState">
          <small>Back the artist(s) a little more <br> by naming your price</small>
          <b-form-input ref="payAmount" class="amount text-center" v-model="amount" :state="inputState" />
        </b-form-group>

        <div class="info text-center" v-if="!canPayMore">
          <div class="msg">
            {{ payLabel }}
          </div>
          <div class="price">
            {{ priceLabel }}
          </div>
        </div>

        <div class="text-center">
          <b-button class="btn-rainbow" @click="showModal" :disabled="!sendEnabled">
            submit
          </b-button>
          <font-awesome-icon icon="spinner" size="lg" spin v-if="working" />
        </div>
      </div>
    </b-popover>

    <b-button class="btn-rainbow purchase available" v-if="isLoggedIn && isAvailable && hasAccessTo" @click="download">
      <font-awesome-icon icon="check" v-if="!this.live" />
      <span v-if="!this.live">&nbsp;</span>
      <div class="label"><span class="normal">{{ accessLabel }}</span></div>
    </b-button>

    <CreditCardLinked ref="ccLinked" />
    <b-modal id="credit-confirm-modal"
             ref="credit-confirm-modal"
             title="Confirm Transaction"
             @cancel="hideModal"
             @ok="pay"
             centered>
      <div class="content">
        <p>
          ${{ amount | decimals }} will be charged to your credit card.
        </p>
      </div>
    </b-modal>
  </div>
</template>

<style scoped lang="scss">
  @import '@/assets/custom.scss';
  @import '~bootstrap/scss/mixins/_breakpoints.scss';

  #credit-confirm-modal {
    font-family: AvenirLight,Muli,Helvetica,sans-serif !important;
    text-align: left !important;
  }
  .pay {
    display: inline-block;

    .btn {
      display: flex;
      align-items: center;
      @include media-breakpoint-down(sm) {
        padding: 0.5em 1em;
        .label {
          /* display: none; */
        }
      }
    }
  }

  .purchase {
    font-family: 'AvenirHeavy';
    padding: 0.5em 1.5em 0.5em 1.5em;
    margin-left: 1em;

    &.available {
      background-image: linear-gradient(107deg, $dark-sky-blue, $aqua-marine);
    }
  }

  .buyPopover {
    min-width: 10em;
    text-align: left;

    img {
      width: 1.2em;
      margin-right: 0.5em;
    }

    .amount {
      max-width: 7em;
      margin: 0 auto;
    }

    .info {
      padding: 0.5em;
      text-align: left;

      .price {
        font-size: 1.2em;
        font-weight: bold;
        font-family: 'AvenirHeavy';
      }
    }

    small {
      display: block;
      font-size: 0.8em;
      line-height: 1.2em;
      padding-bottom: 0.5em;
      color: $steel;
    }
  }

</style>

<script>
import { mapGetters } from 'vuex'
import moment from '@/utils/moment'
import map from 'lodash/map'
import find from 'lodash/find'

import FontAwesomeIcon from '@fortawesome/vue-fontawesome'
import askForCard from '@/utils/stripe'
import CreditCardLinked from '@/components/widgets/CreditCardLinked'

export default {
  name: 'Buy',
  components: {
    FontAwesomeIcon,
    CreditCardLinked
  },
  props: {
    video: Object,
    live: Object,
    album: Object
  },
  data () {
    return {
      btnId: 'btnPurchase' + btoa(Math.random()).substr(3, 20),
      hasAccessTo: false,
      working: false,
      amount: 0
    }
  },
  filters: {
    decimals (value) {
      let txt = value.trim()
      if (txt[0] === '$') txt = txt.substr(1).trim()
      return Number(txt).toFixed(2)
    }
  },
  computed: {
    isAvailable () {
      if (this.price === null) return false
      if (this.video) return this.video.has_digital_download
      if (this.live) return (moment(this.live.start).isBefore() && this.live.is_live)
      return true
    },
    unavailableLabel () {
      if (this.live) return this.live.has_ended ? 'stream finished' : 'stream not started'
      return 'unavailable'
    },
    purchaseLabel () {
      return this.live ? 'get access' : 'download'
    },
    accessLabel () {
      return this.live ? 'join' : 'download'
    },
    price () {
      if (this.video) return this.video.track_price
      if (this.live) return this.live.price
      if (this.album) return this.album.price
      return null
    },
    priceLabel () {
      const price = this.price
      if (price === 0) return 'FREE'
      return '$ ' + price.toFixed(2)
    },
    canPayMore () {
      if (this.video) return this.video.can_pay_more
      if (this.live) return this.live.can_pay_more
      if (this.album) return this.album.can_pay_more
    },
    payLabel () {
      if (this.video) return 'Download audio'
      if (this.live) return 'Get access'
      if (this.album) return 'Download album'
      return 'Purchase'
    },
    minAmountPlaceholder () {
      return 'Minimum $' + this.price.toFixed(2)
    },
    txtAmount () {
      return '$ ' + this.numAmount
    },
    numAmount () {
      if (!this.canPayMore) return this.price

      let txt = this.amount.trim()
      if (!txt) return this.price

      if (txt[0] === '$') txt = txt.substr(1).trim()
      return +txt
    },
    inputState () {
      if (this.numAmount < this.price) return 'invalid'
      return 'valid'
    },
    sendEnabled () {
      return (!this.canPayMore || (this.inputState === 'valid'))
    },
    ...mapGetters('auth', ['isLoggedIn'])
  },
  watch: {
    price () {
      this.closePopup()
      this.checkHasAccessTo()
    },
    album () {
      this.checkHasAccessTo()
    }
  },
  created () {
    this.amount = this.price.toFixed(2)
    this.checkHasAccessTo()
  },
  methods: {
    showModal () {
      this.$refs['credit-confirm-modal'].show()
    },
    hideModal () {
      this.$refs['credit-confirm-modal'].hide()
    },
    async checkHasAccessTo () {
      if (!this.isLoggedIn) return

      if (this.video) {
        const songIds = map(await this.$store.dispatch('song/getSongsWithAccess'), acc => acc.song.id)
        this.hasAccessTo = songIds.indexOf(this.video.id) !== -1
        return
      }
      if (this.live) {
        const ticketReq = await this.$store.dispatch('live/listTickets')
        let ticket = find(await ticketReq.all(), t => t.live_event.id === this.live.id)
        ticket = ticket || null
        this.$emit('ticket', ticket)
        this.hasAccessTo = ticket !== null
        return
      }

      if (this.album) {
        const songIds = map(await this.$store.dispatch('song/getSongsWithAccess'), acc => acc.song.id)
        let hasAccess = true
        for (let song of this.album.songs) {
          if (songIds.indexOf(song.id) === -1) {
            hasAccess = false
            break
          }
        }
        this.hasAccessTo = hasAccess
      }
    },
    async pay () {
      if (!this.sendEnabled) return

      this.hideModal()
      this.working = true

      const amount = this.canPayMore ? this.numAmount : null

      if (!this.$store.getters['profile/currentUser'].stripe_customer_id && this.numAmount) {
        const dialog = askForCard(this.$store)

        try {
          await dialog.finish
          this.$refs.ccLinked.show()
        } catch (e) {
          console.log('can\'t capture cc', e)
          this.working = false
          return
        }
      }

      let request

      if (this.video) {
        request = this.$store.dispatch('song/purchaseSong', {id: this.video.id, amount: amount})
      } else if (this.live) {
        request = this.$store.dispatch('live/bookLiveEvent', {id: this.live.id, amount: amount})
      } else if (this.album) {
        request = this.$store.dispatch('song/purchaseAlbum', {id: this.album.id, amount: amount})
      } else {
        console.log('[pay] error, nothing to pay for')
        this.working = false
        this.closePopup()
        return
      }

      let purchased = false

      try {
        await request
        purchased = true
      } catch (err) {
        let error = 'Error making payment'
        if (err.response && err.response.data && err.response.data.message) {
          error = err.response.data.message
        }
        this.$store.dispatch('flash/showMessage', {message: error, type: 'error'})
      }

      this.closePopup()
      this.working = false
      this.amount = ''

      if (purchased) {
        if (this.live) {
          this.$store.dispatch('flash/showMessage', {message: 'Access granted!'})
        } else {
          this.$store.dispatch('flash/showMessage', {message: 'File downloaded!'})
        }
        this.$emit('purchase')
        this.checkHasAccessTo()
      }
    },
    closePopup () {
      const el = this.$refs.popover
      if (el) el.$emit('close')
    },
    flashUnavailable () {
      if (this.video) {
        this.$store.dispatch('flash/showMessage', {
          message: 'Audio file unavailable for download',
          type: 'error'
        })
      }
      if (this.album) {
        this.$store.dispatch('flash/showMessage', {
          message: 'The album can\'t be purchased as a whole',
          type: 'error'
        })
      }
      if (this.live) {
        let msg = 'Can\'t book the live stream at this time'
        let bookingStart = moment(this.live.booking_start)
        if (this.live.has_ended) msg = 'Live stream has finished'
        if (bookingStart.isAfter()) msg = 'Live stream booking starts ' + bookingStart.fromNow()
        this.$store.dispatch('flash/showMessage', {
          message: msg,
          type: 'error'
        })
      }
    },
    async download () {
      if (!this.hasAccessTo) return

      if (this.video) {
        const songAccess = find(
          await this.$store.dispatch('song/getSongsWithAccess'),
          acc => acc.song.id === this.video.id
        )

        if (songAccess) window.location = songAccess.download_url
        return
      }

      if (this.album) {
        this.$router.push({name: 'fan-downloads'})
        return
      }

      if (this.live) {
        this.$emit('join', this.live)
      }
    }
  }
}
</script>



// WEBPACK FOOTER //
// src/components/widgets/Pay.vue